import { useDispatch, useSelector } from "react-redux";
import { documentclassification } from "../../utils/fieldConstants";
import { CloseOutActions } from "../../redux/closeoutslice/closeoutSlice";
import { uploadChunk } from "../../services/uploadService";
import { restrictedUploadChunk } from "../../services/restrictedUploadService";

const UseUploadInChunks = () => {
  const dispatch = useDispatch();
  const { gpid } = useSelector((state: any) => state.auth);
  const { uploadedFilesSuccess } = useSelector((state: any) => state.closeOut);
  // If file is already uploaded, don't re-upload it
  const fileUploadInChuncks = async (
    file: File,
    constructedFiles: any[],
    classification: string
  ) => {
    if (uploadedFilesSuccess.includes(file.name)) {
      return;
    }

    dispatch(
      CloseOutActions.setUploadProgress({ fileName: file.name, progress: 0 })
    );

    // Check if the file is empty (0KB)
    if (file.size === 0) {
      try {
        // Simulate a chunk upload for 0KB file (by calling uploadChunk with zero data)
        const start = 0;
        // Handle empty file upload based on classification
        let response;
        if (classification === "confidential") {
          response = await uploadChunk(file, start, 1, gpid); // uploadChunk for confidential files
        } else if (classification === "internal") {
          response = await uploadChunk(file, start, 3, gpid); // uploadChunk for internal files
        } else {
          response = await restrictedUploadChunk(file, start, 2, gpid); // uploadRestrictedFile for restricted files
        }

        const { responseBody } = response;

        // Parse the response body correctly
        let responseJson;
        if (typeof responseBody === "string") {
          responseJson = JSON.parse(responseBody);
        } else {
          responseJson = responseBody; // responseBody is already an object
        }

        // Since it's a 0KB file, we can mark the file as uploaded right away
        const finalBlobFileName = responseJson?.newFileName || file.name;
        dispatch(
          CloseOutActions.setUploadedfileSuccess([
            ...uploadedFilesSuccess,
            file.name,
          ])
        );
        dispatch(
          CloseOutActions.setUploadProgress({
            fileName: file.name,
            progress: 100,
          })
        );

        // Construct the file metadata after the upload response
        const fileObject = {
          blob_file_name: finalBlobFileName, // The response gives the blob file name
          file_name: file.name, // File name
          document_classification:
            classification === "confidential"
              ? documentclassification.confidential
              : classification === "restricted"
              ? documentclassification.restricted
              : classification === "internal"
              ? documentclassification.internal
              : null, // Internal
          user_id: gpid, // User ID
          file_size: file.size, // File size
          file_type: file.name.split(".").pop(), // File extension
          mime_type: `application/${file.name.split(".").pop()}`.toLowerCase(),
        };

        // Add the constructed metadata to the list of files
        constructedFiles.push(fileObject);

        return; // Exit the function as the 0KB file is processed
      } catch (error) {
        dispatch(
          CloseOutActions.setErrorMessage(
            "Error uploading file. Please try again."
          )
        );
        dispatch(CloseOutActions.setIsuploading(false));
        return;
      }
    }

    let start = 0;
    let uploadedBytes = 0;
    const totalSize = file.size; // Total size of the file
    const documentClassification =
      classification === "confidential"
        ? 1
        : classification === "restricted"
        ? 2
        : 3;

    let fileUploaded = false; // Flag to check if file has been added to constructedFiles
    let finalBlobFileName = ""; // To hold the newFileName after the last chunk

    const chunkList: string[] = []; // Initialize chunkList to keep track of chunks
    const dotIndex = file.name.lastIndexOf(".");
    const fileNameWithoutExtension = file.name.substring(0, dotIndex);
    const fileExtension = file.name.substring(dotIndex + 1);
    const fileId = `${sanitizeFilename(
      fileNameWithoutExtension
    )}_${Date.now()}.${fileExtension}`; // Generate a unique fileId

    while (start < file.size) {
      chunkList.push(`chunk_${start}`);
      try {
        // If file is confidential, use uploadChunk, else use another API (e.g., uploadRestrictedFile)
        let response;
        if (
          classification === "confidential" ||
          classification === "internal"
        ) {
          response = await uploadChunk(
            file,
            start,
            documentClassification,
            gpid,
            fileId,
            chunkList
          );
        } else {
          response = await restrictedUploadChunk(
            file,
            start,
            documentClassification,
            gpid,
            fileId,
            chunkList
          ); // Assume this is a different API for restricted files
        }

        const { end, responseBody } = response;

        // Parse the response body correctly
        let responseJson;
        if (typeof responseBody === "string") {
          responseJson = JSON.parse(responseBody);
        } else {
          responseJson = responseBody; // responseBody is already an object
        }

        // If this is the last chunk, extract newFileName
        if (end >= totalSize) {
          finalBlobFileName = responseJson?.newFileName || fileId;
        }

        // Update the number of uploaded bytes
        uploadedBytes += end - start;
        start = end;

        // Calculate progress percentage
        const progressPercentage = ((uploadedBytes / totalSize) * 100).toFixed(
          2
        );
        dispatch(
          CloseOutActions.setUploadProgress({
            fileName: file.name,
            progress: parseFloat(progressPercentage),
          })
        );

        // Mark file as uploaded when it reaches 100% progress
        if (progressPercentage === "100.00") {
          dispatch(
            CloseOutActions.setUploadedfileSuccess([
              ...uploadedFilesSuccess,
              file.name,
            ])
          );
        }
      } catch (error) {
        dispatch(
          CloseOutActions.setErrorMessage(
            "Error uploading file. Please try again."
          )
        );
        dispatch(CloseOutActions.setIsuploading(false));
        return;
      }
    }

    // Only add the file object once all chunks are uploaded and we have the final blobFileName
    if (finalBlobFileName && !fileUploaded) {
      const fileObject = {
        blob_file_name: finalBlobFileName, // From the last chunk response
        file_name: file.name, // From file
        document_classification: documentClassification, // Classification (confidential or restricted or internal)
        user_id: gpid, // Static gpid
        file_size: file.size, // From file
        file_type: file.name.split(".").pop(), // File extension
        mime_type: `application/${file.name.split(".").pop()}`.toLowerCase(),
      };

      // Add the constructed metadata to the list of files
      constructedFiles.push(fileObject);
      fileUploaded = true; // Set flag to true after adding the file object
    }
  };
  return { fileUploadInChuncks };
};
export default UseUploadInChunks;

export const sanitizeFilename = (filename: string) => {
  return filename.replace(/[^a-zA-Z0-9.\-]/g, "");
};
